import { useEffect, useMemo, useState } from "react";
import { useModals } from "../components/common/ModalsProvider.js";
import { BankCard, BankShell, bankInputClass } from "../components/bank/Shell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useT } from "../i18n/index.js";
import { formatMoney, toNumber } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { Paginacao } from "../components/common/Paginacao.js";
import { CHIP_ON, FILTER_INPUT, FilterField } from "../components/common/FilterBar.js";

const PAGE_SIZE = 50;

const STATUS_TONE: Record<string, string> = {
  pending: "bg-sky-500/15 text-sky-300",
  accepted: "bg-lime/15 text-lime",
  rejected: "bg-red-500/15 text-red-300",
};

export default function BankReconciliacao() {
  const { user } = useAuth();
  const modals = useModals();
  const t = useT();
  const kindLabels: Record<string, string> = {
    customer_receipt: t("bankReconciliation.kindCustomerReceipt"),
    technician_payment: t("bankReconciliation.kindTechnicianPayment"),
    tax_payment: t("bankReconciliation.kindTaxPayment"),
    intercompany_invoice: t("bankReconciliation.kindIntercompany"),
    other: t("bankReconciliation.kindOther"),
    // F-bank-drives-paid — aceitar CRIA o recibo e marca a fatura/dossier paga.
    customer_invoice: t("bankReconciliation.kindCustomerInvoice"),
  };
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [minConfidence, setMinConfidence] = useState<string>("0.5");
  const [offset, setOffset] = useState(0);

  const filters = useMemo(
    () => ({
      companyId: companyId ?? 0,
      status:
        statusFilter === "all" ? undefined : (statusFilter as "pending" | "accepted" | "rejected"),
      minConfidence: minConfidence ? Number(minConfidence) : undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [companyId, statusFilter, minConfidence, offset],
  );

  const listQuery = trpc.reconciliation.listSuggestions.useQuery(filters, {
    enabled: companyId !== null,
  });

  // Pré-carrega tx envolvidas (até PAGE_SIZE)
  const txIds = (listQuery.data?.rows ?? []).map((s) => s.bankTransactionId);
  const txsQuery = trpc.bankTransactions.list.useQuery(
    {
      companyId: companyId ?? 0,
      limit: PAGE_SIZE,
      offset: 0,
    },
    { enabled: companyId !== null && txIds.length > 0 },
  );

  const utils = trpc.useUtils();
  const acceptMutation = trpc.reconciliation.acceptSuggestion.useMutation({
    onSuccess: () => {
      utils.reconciliation.invalidate();
      utils.bankTransactions.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: t("bankReconciliation.error"),
        message: e.message,
        tone: "danger",
      }),
  });
  const rejectMutation = trpc.reconciliation.rejectSuggestion.useMutation({
    onSuccess: () => utils.reconciliation.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("bankReconciliation.error"),
        message: e.message,
        tone: "danger",
      }),
  });
  const unrejectMutation = trpc.reconciliation.unreject.useMutation({
    onSuccess: () => utils.reconciliation.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("bankReconciliation.error"),
        message: e.message,
        tone: "danger",
      }),
  });
  const generateMutation = trpc.reconciliation.generateSuggestions.useMutation({
    onSuccess: (res) => {
      utils.reconciliation.invalidate();
      void modals.alert({
        message: `${t("bankReconciliation.scanLabel")}: ${res?.scanned ?? 0} ${t("bankReconciliation.scanTx")} · ${res?.created ?? 0} ${t("bankReconciliation.scanCreated")} · ${res?.updatedInPlace ?? 0} ${t("bankReconciliation.scanUpdated")} · ${res?.noCandidate ?? 0} ${t("bankReconciliation.scanNoCandidate")} · ${res?.blockedRejected ?? 0} ${t("bankReconciliation.scanBlockedRejected")} · ${res?.blockedAccepted ?? 0} ${t("bankReconciliation.scanBlockedAccepted")}`,
      });
    },
    onError: (e) =>
      void modals.alert({
        title: t("bankReconciliation.error"),
        message: e.message,
        tone: "danger",
      }),
  });

  if (!user) return null;
  if (accessibleCompanies.length === 0) {
    return (
      <BankShell subtitle={t("bankReconciliation.reconciliationSubtitle")}>
        <p className="text-sm text-neutral-400">{t("bankReconciliation.noCompanyAccess")}</p>
      </BankShell>
    );
  }

  const isAdmin =
    user.isSuperAdmin ||
    user.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin");

  const data = listQuery.data;
  const total = data?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const highConfThreshold = data?.highConfidenceThreshold ?? 0.85;

  // Mapa tx_id → tx info
  const txMap = new Map((txsQuery.data?.rows ?? []).map((t) => [t.id, t]));

  const toolbar = (
    <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
      <FilterField label={t("bankReconciliation.filterCompany")}>
        <select
          value={companyId ?? ""}
          onChange={(e) => {
            setCompanyId(Number(e.target.value));
            setOffset(0);
          }}
          className={FILTER_INPUT + " w-60"}
        >
          {accessibleCompanies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </FilterField>
      <FilterField label={t("bankReconciliation.filterStatus")}>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setOffset(0);
          }}
          className={FILTER_INPUT + " w-36"}
        >
          <option value="pending">{t("bankReconciliation.statusPending")}</option>
          <option value="accepted">{t("bankReconciliation.statusAccepted")}</option>
          <option value="rejected">{t("bankReconciliation.statusRejected")}</option>
          <option value="all">{t("bankReconciliation.statusAll")}</option>
        </select>
      </FilterField>
      <FilterField label={t("bankReconciliation.minConfidenceLabel")}>
        <input
          type="number"
          step="0.05"
          min="0"
          max="1"
          value={minConfidence}
          onChange={(e) => {
            setMinConfidence(e.target.value);
            setOffset(0);
          }}
          className={FILTER_INPUT + " w-24 text-right font-mono"}
        />
      </FilterField>
      <div className="flex-1" />
      <button
        onClick={() => {
          if (!companyId) return;
          generateMutation.mutate({ mode: "unreconciled", companyId });
        }}
        disabled={generateMutation.isPending}
        className={CHIP_ON + " disabled:opacity-50"}
      >
        {generateMutation.isPending
          ? t("bankReconciliation.generating")
          : `↻ ${t("bankReconciliation.generateBtn")}`}
      </button>
    </div>
  );

  return (
    <BankShell subtitle={t("bankReconciliation.suggestionsSubtitle")} toolbar={toolbar}>
      <BankCard
        title={`${total} ${t("bankReconciliation.suggestionsCount")}`}
        subtitle={`${t("bankReconciliation.pageLabel")} ${page} ${t("bankReconciliation.pageOf")} ${totalPages} · ${t("bankReconciliation.highConfidence")} ≥ ${highConfThreshold}`}
        padded={false}
      >
        {listQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("bankReconciliation.loading")}</p>
        )}
        {!listQuery.isLoading && (data?.rows.length ?? 0) === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("bankReconciliation.emptyState")}</p>
        )}
        {(data?.rows.length ?? 0) > 0 && (
          <div className="overflow-x-auto">
            <table className="dz-tbl w-full">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2 w-24">
                    {t("bankReconciliation.colConfidence")}
                  </th>
                  <th className="text-left px-3 py-2">
                    {t("bankReconciliation.colBankTransaction")}
                  </th>
                  <th className="text-right px-3 py-2 w-28 font-mono">
                    {t("bankReconciliation.colAmount")}
                  </th>
                  <th className="text-left px-3 py-2 w-32">
                    {t("bankReconciliation.colSuggestion")}
                  </th>
                  <th className="text-right px-3 py-2 w-16 font-mono">
                    {t("bankReconciliation.colTargetId")}
                  </th>
                  <th className="text-left px-3 py-2">{t("bankReconciliation.colReason")}</th>
                  <th className="text-left px-3 py-2 w-24">{t("bankReconciliation.colStatus")}</th>
                  <th className="px-3 py-2 w-44 text-right">
                    {t("bankReconciliation.colActions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {(data?.rows ?? []).map((s) => {
                  const tx = txMap.get(s.bankTransactionId);
                  const conf = Number(s.confidenceScore);
                  const reasons = Array.isArray(s.matchReasons) ? (s.matchReasons as string[]) : [];
                  return (
                    <tr key={s.id}>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-xs font-mono ${
                            conf >= highConfThreshold
                              ? "bg-lime/15 text-lime"
                              : conf >= 0.7
                                ? "bg-sky-500/15 text-sky-300"
                                : "bg-amber-500/15 text-amber-300"
                          }`}
                        >
                          {conf.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {tx ? (
                          <>
                            <div className="text-xs text-neutral-400">{tx.transactionDate}</div>
                            <div
                              className="text-neutral-200 max-w-sm whitespace-normal break-words"
                              title={tx.description ?? ""}
                            >
                              {tx.description || "—"}
                            </div>
                          </>
                        ) : (
                          <span className="text-neutral-700 text-xs">
                            tx #{s.bankTransactionId}
                          </span>
                        )}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono tabular-nums ${
                          tx && Number(tx.amount) > 0
                            ? "text-lime"
                            : tx && Number(tx.amount) < 0
                              ? "text-red-400"
                              : "text-neutral-400"
                        }`}
                      >
                        {tx ? formatMoney(toNumber(tx.amount) ?? 0, { decimals: 2 }) : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {s.suggestedKind === "customer_invoice" ? (
                          <span
                            className="text-lime"
                            title={t("bankReconciliation.acceptCreatesReceiptTitle")}
                          >
                            {kindLabels.customer_invoice}
                          </span>
                        ) : (
                          <span className="text-neutral-300">
                            {kindLabels[s.suggestedKind] ?? s.suggestedKind}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-neutral-400">
                        #{s.suggestedId}
                      </td>
                      <td className="px-3 py-2 text-xs text-neutral-400 max-w-md">
                        <div className="whitespace-normal break-words" title={reasons.join(" · ")}>
                          {reasons.join(" · ")}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_TONE[s.status]}`}
                        >
                          {s.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          {s.status === "pending" && (
                            <>
                              <button
                                onClick={() => {
                                  if (!companyId) return;
                                  acceptMutation.mutate({
                                    companyId,
                                    suggestionId: s.id,
                                  });
                                }}
                                disabled={acceptMutation.isPending}
                                className="rounded-md border border-lime/40 hover:border-lime text-lime px-2 py-0.5 text-xs"
                              >
                                {t("bankReconciliation.acceptBtn")}
                              </button>
                              <button
                                onClick={async () => {
                                  const reason = await modals.promptReason({
                                    title: t("bankReconciliation.rejectTitle"),
                                    message: t("bankReconciliation.rejectMessage"),
                                    placeholder: t("bankReconciliation.rejectPlaceholder"),
                                    confirmLabel: t("bankReconciliation.rejectConfirm"),
                                    tone: "danger",
                                    required: true,
                                  });
                                  if (!reason || !companyId) return;
                                  rejectMutation.mutate({
                                    companyId,
                                    suggestionId: s.id,
                                    reason,
                                  });
                                }}
                                disabled={rejectMutation.isPending}
                                className="rounded-md border border-red-500/40 hover:border-red-500 text-red-300 px-2 py-0.5 text-xs"
                              >
                                {t("bankReconciliation.rejectBtn")}
                              </button>
                            </>
                          )}
                          {s.status === "rejected" && isAdmin && (
                            <button
                              onClick={async () => {
                                if (!companyId) return;
                                const reason = await modals.promptReason({
                                  title: t("bankReconciliation.unrejectTitle"),
                                  message: t("bankReconciliation.unrejectMessage"),
                                  placeholder: t("bankReconciliation.unrejectPlaceholder"),
                                  confirmLabel: t("bankReconciliation.unrejectConfirm"),
                                  required: true,
                                });
                                if (!reason) return;
                                unrejectMutation.mutate({
                                  companyId,
                                  suggestionId: s.id,
                                  reason,
                                });
                              }}
                              disabled={unrejectMutation.isPending}
                              className="rounded-md border border-amber-500/40 hover:border-amber-500 text-amber-300 px-2 py-0.5 text-xs"
                            >
                              {t("bankReconciliation.unrejectBtn")}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </BankCard>

      {total > 0 && (
        <Paginacao
          total={total}
          pageSize={PAGE_SIZE}
          offset={offset}
          onOffset={setOffset}
          testId="bankReconciliacao-pagination"
        />
      )}
    </BankShell>
  );
}
